import { useState, useEffect } from "react";
import { Plus, Trash2, Zap, Stethoscope, ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { detectCptCode } from "@/lib/cptLookup";
import { formatNumber } from "@/lib/constants";
import { addToQuickAddCptList } from "@/lib/api";
import { toast } from "sonner";
import { useTheme } from "@/contexts/ThemeContext";

// Context Menu for entries
function EntryContextMenu({ x, y, entry, onClose, onAddToQuickAdd }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  useEffect(() => {
    const handleClick = () => onClose();
    const handleEscape = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('click', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div 
      className={`fixed z-[200] border rounded-lg shadow-xl py-1 min-w-[200px] ${
        isDark ? 'bg-slate-800 border-slate-600' : 'bg-white border-gray-200'
      }`}
      style={{ left: x, top: y }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className={`px-3 py-2 border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
        <p className={`text-xs truncate max-w-[180px] ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
          {entry.label}
        </p>
        <p className={`text-xs font-mono mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
          {entry.wrvus} wRVU
        </p>
      </div>
      <button
        onClick={() => { onAddToQuickAdd(entry); onClose(); }}
        className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors text-left ${
          isDark 
            ? 'text-slate-200 hover:bg-blue-500/20 hover:text-blue-300'
            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
        }`}
      >
        <ListPlus size={14} className={isDark ? 'text-blue-400' : 'text-blue-600'} />
        Add to Quick-Add CPT List
      </button>
    </div>
  );
}

export default function ProcedureEntries({ entries, onAdd, onRemove, onUpdate, onCptListUpdated }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [matchedCodes, setMatchedCodes] = useState({});
  const [contextMenu, setContextMenu] = useState(null);

  // Extract CPT code from label
  const extractCptCode = (label) => {
    const match = label.match(/(\d{5})/);
    return match ? match[1] : null;
  };

  const handleContextMenu = (e, entry) => {
    e.preventDefault();
    const code = extractCptCode(entry.label);
    if (code) {
      setContextMenu({ x: e.clientX, y: e.clientY, entry });
    }
  };

  const handleAddToQuickAdd = async (entry) => {
    const code = extractCptCode(entry.label);
    if (!code) {
      toast.error("Could not extract CPT code from entry");
      return;
    }
    const description = entry.label.replace(/^\d{5}\s*[—-]?\s*/, "").trim();
    try {
      await addToQuickAddCptList(code, description, parseFloat(entry.wrvus) || 0);
      toast.success(`Added ${code} to Quick-Add CPT List`);
      // Trigger refresh of the CPT panel
      if (onCptListUpdated) onCptListUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add to Quick-Add CPT List");
    }
  };

  const handleLabelChange = (id, value) => {
    onUpdate(id, "label", value);

    const result = detectCptCode(value);
    if (result) {
      // Auto-fill the label with CPT name and set wRVU
      onUpdate(id, "label", `${result.code} — ${result.name}`);
      onUpdate(id, "wrvus", String(result.wrvu));
      setMatchedCodes(prev => ({ ...prev, [id]: result.code }));
    } else {
      setMatchedCodes(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div data-testid="procedure-entries-section" className={`rounded-xl border-2 p-6 mb-8 ${
      isDark 
        ? 'border-teal-500/20 bg-teal-500/5'
        : 'border-teal-200 bg-teal-50/30'
    }`}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="flex items-center gap-2">
            <Stethoscope size={24} className={isDark ? 'text-teal-400' : 'text-teal-600'} />
            <h3 className={`text-2xl md:text-3xl font-bold ${
              isDark ? 'text-teal-300' : 'text-teal-700'
            }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
              Procedures & Services
            </h3>
          </div>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            Type a 5-digit CPT code to auto-fill name and wRVUs
          </p>
          <p className={`text-xs italic ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>
            Adds wRVUs only — does not count toward patient total
          </p>
        </div>
        <Button
          data-testid="add-procedure-entry-btn"
          onClick={onAdd}
          className={`border transition-colors h-12 px-5 text-base font-semibold ${
            isDark 
              ? 'bg-teal-600/30 hover:bg-teal-600/50 text-teal-300 border-teal-500/30 hover:border-teal-400/50'
              : 'bg-teal-100 hover:bg-teal-200 text-teal-700 border-teal-300 hover:border-teal-500'
          }`}
        >
          <Plus size={20} className="mr-2" />
          Add Procedure
        </Button>
      </div>

      {entries.length === 0 && (
        <p data-testid="procedure-empty" className={`text-base italic py-6 text-center ${
          isDark ? 'text-slate-600' : 'text-gray-500'
        }`}>
          No procedures yet — click "Add Procedure" to log one
        </p>
      )}

      <div className="space-y-4">
        {entries.map((entry, index) => (
          <div key={entry.id}>
            <div
              data-testid={`procedure-entry-${index}`}
              className="flex items-center gap-4 animate-slide-up"
              style={{ animationDelay: '0s' }}
              onContextMenu={(e) => handleContextMenu(e, entry)}
            >
              <div className="flex-1 relative">
                <Input
                  data-testid={`procedure-label-${index}`}
                  placeholder="Procedure name or CPT code..."
                  value={entry.label}
                  onChange={(e) => handleLabelChange(entry.id, e.target.value)}
                  className={`border text-base h-12 ${
                    isDark 
                      ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-teal-500/50 text-slate-200 placeholder:text-slate-600'
                      : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-teal-500 text-gray-900 placeholder:text-gray-400'
                  }`}
                />
              </div>
              <div className="w-44">
                <div className="relative">
                  <Input
                    data-testid={`procedure-wrvu-${index}`}
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={entry.wrvus || ""}
                    onChange={(e) => onUpdate(entry.id, "wrvus", e.target.value)}
                    className={`border font-mono text-base h-12 pr-16 ${
                      isDark 
                        ? 'bg-slate-800/50 border-slate-700/50 hover:border-slate-600 focus:border-teal-500/50 text-teal-300 placeholder:text-slate-600'
                        : 'bg-gray-50 border-gray-300 hover:border-gray-400 focus:border-teal-500 text-teal-700 placeholder:text-gray-400'
                    }`}
                  />
                  <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-mono ${
                    isDark ? 'text-slate-500' : 'text-gray-500'
                  }`}>wRVU</span>
                </div>
              </div>
              <Button
                data-testid={`procedure-delete-${index}`}
                size="icon"
                variant="ghost"
                onClick={() => onRemove(entry.id)}
                className={`h-12 w-12 flex-shrink-0 ${
                  isDark 
                    ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10'
                    : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                }`}
              >
                <Trash2 size={18} />
              </Button>
            </div>
            {matchedCodes[entry.id] && (
              <div className="flex items-center gap-1.5 mt-1.5 ml-1">
                <Zap size={12} className={isDark ? 'text-emerald-400' : 'text-emerald-600'} />
                <span className={`text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                  Auto-filled from CPT {matchedCodes[entry.id]}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>

      {entries.length > 0 && (
        <div className={`mt-5 pt-4 border-t flex items-center justify-between ${
          isDark ? 'border-teal-500/10' : 'border-teal-200'
        }`}>
          <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            {entries.length} procedure{entries.length !== 1 ? 's' : ''}
          </span>
          <span className={`text-base font-mono font-bold ${
            isDark ? 'text-teal-300' : 'text-teal-700'
          }`}>
            {formatNumber(entries.reduce((sum, e) => sum + (parseFloat(e.wrvus) || 0), 0))} wRVU
          </span>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <EntryContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          entry={contextMenu.entry}
          onClose={() => setContextMenu(null)}
          onAddToQuickAdd={handleAddToQuickAdd}
        />
      )}
    </div>
  );
}
